import { DataContext } from '@/types';
import { HttpTransportType, HubConnectionBuilder } from '@microsoft/signalr';
import { safeTrackException } from '../../appInsights';
import { Dashboard_mode } from '../../constants';
import { Data } from '../Data';
import { Notification } from './Notification';

export class NotificationsConnection extends Data {
  static instance: NotificationsConnection | null = null;

  constructor(options: any) {
    super(options);

    Data.getAccessToken(options.context)
      .then(() => {
        const connection = new HubConnectionBuilder()
          .withUrl(`${(window as any).app.env.API_URL}/live/notifications`, {
            // Acquire a fresh token on every (re)negotiate. MSAL silently refreshes
            // expired tokens, so reconnecting after an idle period no longer 401s.
            accessTokenFactory: () =>
              Data.getAccessToken(options.context).then((token) => token ?? ''),
            transport: HttpTransportType.WebSockets | HttpTransportType.LongPolling,
          })
          .withAutomaticReconnect()
          .build();

        connection.on('receiveNotification', (notif) => {
          const json = JSON.parse(notif);
          this.onNotificationReceived(
            this.parseNotification(json),
            json.NumberOfUnreadNotification
          );
        });

        // Lightweight, parameterless server signal that a skill's matches (re)computed. Surfaced via an
        // assignable callback (like onNotificationReceived) so the transport stays free of query-cache
        // knowledge. Fired on reconnect too, to catch any event missed during a reconnect gap.
        connection.on('skillMatchesComputed', () => this.onSkillMatchesComputed?.());
        connection.onreconnected(() => this.onSkillMatchesComputed?.());

        connection
          .start()
          .then(() => {
            options.resolve(this, false);
          })
          .catch((e) => {
            safeTrackException(e, { source: 'NotificationsConnection', phase: 'start' });
            options.reject(e);
          });
      })
      .catch((e) => {
        safeTrackException(e, { source: 'NotificationsConnection', phase: 'token' });
        options.reject(e);
      });
  }

  parseNotification(json: any) {
    return new Notification(json.Notification, this.getModeFromIndex(json.NotificationType));
  }

  onNotificationReceived(notif: any, numUnread: any) {}

  onSkillMatchesComputed?: () => void;

  getModeFromIndex(index: any) {
    return Object.values(Dashboard_mode)[index];
  }

  static getNotificationsConnection(context: DataContext) {
    return new Promise((resolve, reject) => {
      if (NotificationsConnection.instance) resolve(NotificationsConnection.instance);
      else
        NotificationsConnection.instance = new NotificationsConnection({
          resolve,
          reject,
          context,
        });
    });
  }
}
